/* ==========================================================================
   Two-factor code step (PORTAL-187)
   Shown on a login card after a correct password when the account has
   authenticator-app 2FA on. Shared by the provider, pharmacy-admin and
   internal login pages; each page owns the API call.
   ========================================================================== */

import { useState } from 'react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { FormField } from '@/components/ui/FormField'
import { Checkbox } from '@/components/ui/Checkbox'
import { Alert } from '@/components/ui/Alert'
import { normalizeTwoFactorCode } from '@/utils/twoFactorDevice'

export interface TwoFactorCodeStepProps {
  /** Submit the 6-digit code. */
  onSubmit: (code: string, rememberDevice: boolean) => void | Promise<void>
  /** Go back to the email/password form. */
  onCancel: () => void
  loading?: boolean
  error?: string | null
}

export function TwoFactorCodeStep({
  onSubmit,
  onCancel,
  loading = false,
  error,
}: TwoFactorCodeStepProps) {
  const [code, setCode] = useState('')
  // Unticked by default: remembering skips the code on this browser for 30 days,
  // which is unsafe on a shared computer, so the user has to opt in.
  const [rememberDevice, setRememberDevice] = useState(false)

  const handleSubmit = (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (code.length !== 6) return
    void onSubmit(code, rememberDevice)
  }

  return (
    <div>
      <div className="text-center mb-6">
        <h2 className="font-serif text-xl sm:text-2xl text-charcoal">
          Two-factor authentication
        </h2>
        <p className="text-charcoal/70 mt-2 text-sm">
          Open your authenticator app and enter the 6-digit code.
        </p>
      </div>

      {error && (
        <Alert variant="error" className="mb-6">
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <div className="space-y-5">
          <FormField label="Authentication code" required htmlFor="two-factor-code">
            <Input
              id="two-factor-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="123456"
              maxLength={7}
              value={code}
              onChange={(e) => setCode(normalizeTwoFactorCode(e.target.value))}
              disabled={loading}
              autoFocus
              className="tracking-[0.4em] text-center text-lg"
            />
          </FormField>

          <Checkbox
            label="Don't ask again on this device for 30 days"
            checked={rememberDevice}
            onChange={(e) => setRememberDevice(e.target.checked)}
            disabled={loading}
          />
        </div>

        <Button
          type="submit"
          variant="primary"
          size="lg"
          loading={loading}
          disabled={loading || code.length !== 6}
          className="w-full mt-6"
        >
          Verify
        </Button>
      </form>

      <div className="mt-6 text-center">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="text-sm text-charcoal/70 hover:text-charcoal hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-lavender rounded"
        >
          Back to sign in
        </button>
      </div>
    </div>
  )
}

export default TwoFactorCodeStep
